import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, Save, Trash2 } from "lucide-react";
import { calculateDay } from "../tipCalculator";
import type { DayEntry, DaySummary, Employee, TipEvent } from "../types";
import { employeeDisplayName } from "../employeePeriod";

export interface KitchenTipEntry {
  employee_id: string;
  employee_name: string;
  amount: number;
}

interface Props {
  date: string;
  employees: Employee[];
  kitchenEmployees: Employee[];
  existingTips: Map<string, { hours_worked: number; raw_tips: number }>;
  existingSales: number;
  existingEvents: TipEvent[];
  existingKitchenTipouts: Map<string, Map<string, number>>;
  processingFeePct: number;
  locked: boolean;
  onSave: (data: {
    tips: Array<{ employee_id: string; hours_worked: number; raw_tips: number; calculated_tips: number }>;
    totalSales: number;
    events: Array<{ id?: string; name: string; total_gratuity: number; kitchen_tipout: number }>;
    kitchenTipouts: Array<{ eventName: string; entries: KitchenTipEntry[] }>;
    deletedEventIds: string[];
  }) => Promise<void>;
}

function fmtDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function pct(n: number): string {
  return (n * 100).toFixed(1) + "%";
}

export function DayEditor({
  date,
  employees,
  kitchenEmployees,
  existingTips,
  existingSales,
  existingEvents,
  existingKitchenTipouts,
  processingFeePct,
  locked,
  onSave,
}: Props) {
  const [entries, setEntries] = useState<Record<string, { hours: string; tips: string }>>({});
  const [totalSales, setTotalSales] = useState(existingSales > 0 ? String(existingSales) : "");
  const [events, setEvents] = useState<Array<{
    id?: string;
    name: string;
    total_gratuity: string;
  }>>([]);
  // Per-event, per-kitchen-employee tip amounts: eventIdx → empId → amount string
  const [kitchenTips, setKitchenTips] = useState<Record<number, Record<string, string>>>({});
  const [deletedEventIds, setDeletedEventIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init: Record<string, { hours: string; tips: string }> = {};
    for (const emp of employees) {
      const ex = existingTips.get(emp.id);
      init[emp.id] = {
        hours: ex ? String(ex.hours_worked) : "",
        tips: ex ? String(ex.raw_tips) : "",
      };
    }
    setEntries(init);
    setTotalSales(existingSales > 0 ? String(existingSales) : "");

    const evList = existingEvents.map((ev) => ({
      id: ev.id,
      name: ev.name,
      total_gratuity: String(ev.total_gratuity),
    }));
    setEvents(evList);

    const ktInit: Record<number, Record<string, string>> = {};
    evList.forEach((ev, idx) => {
      const perEmp: Record<string, string> = {};
      const existing = existingKitchenTipouts.get(ev.name ?? "");
      for (const ke of kitchenEmployees) {
        perEmp[ke.id] = existing?.get(ke.id) != null ? String(existing.get(ke.id)) : "";
      }
      ktInit[idx] = perEmp;
    });
    setKitchenTips(ktInit);

    setDeletedEventIds([]);
  }, [date, employees, kitchenEmployees, existingTips, existingSales, existingEvents, existingKitchenTipouts]);

  const dayEntries: DayEntry[] = useMemo(
    () =>
      employees.map((emp) => ({
        employee_id: emp.id,
        employee_name: emp.name,
        hours_worked: parseFloat(entries[emp.id]?.hours || "0") || 0,
        raw_tips: parseFloat(entries[emp.id]?.tips || "0") || 0,
      })),
    [employees, entries],
  );

  function kitchenTotalForEvent(idx: number): number {
    const perEmp = kitchenTips[idx];
    if (!perEmp) return 0;
    return Object.values(perEmp).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  }

  const eventNetToPool = useMemo(() => {
    return events.reduce((sum, ev, idx) => {
      const grat = parseFloat(ev.total_gratuity) || 0;
      const kit = kitchenTotalForEvent(idx);
      return sum + Math.max(0, grat - kit);
    }, 0);
  }, [events, kitchenTips]);

  const calc: DaySummary = useMemo(
    () => calculateDay(dayEntries, processingFeePct, eventNetToPool),
    [dayEntries, processingFeePct, eventNetToPool],
  );

  function setField(empId: string, field: "hours" | "tips", value: string) {
    setEntries((prev) => ({ ...prev, [empId]: { ...prev[empId], [field]: value } }));
  }

  function addEvent() {
    const newIdx = events.length;
    setEvents((prev) => [...prev, { name: "", total_gratuity: "" }]);
    const perEmp: Record<string, string> = {};
    for (const ke of kitchenEmployees) perEmp[ke.id] = "";
    setKitchenTips((prev) => ({ ...prev, [newIdx]: perEmp }));
  }

  function removeEvent(idx: number) {
    setEvents((prev) => {
      const removed = prev[idx];
      if (removed.id) setDeletedEventIds((d) => [...d, removed.id!]);
      return prev.filter((_, i) => i !== idx);
    });
    setKitchenTips((prev) => {
      const next: Record<number, Record<string, string>> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki < idx) next[ki] = v;
        else if (ki > idx) next[ki - 1] = v;
      }
      return next;
    });
  }

  function setEventField(idx: number, field: string, value: string) {
    setEvents((prev) =>
      prev.map((ev, i) => (i === idx ? { ...ev, [field]: value } : ev)),
    );
  }

  function setKitchenTip(eventIdx: number, empId: string, value: string) {
    setKitchenTips((prev) => ({
      ...prev,
      [eventIdx]: { ...prev[eventIdx], [empId]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const filteredEvents = events
        .map((ev, idx) => ({ ev, idx }))
        .filter(({ ev }) => ev.name.trim());

      await onSave({
        tips: calc.entries.map((e) => ({
          employee_id: e.employee_id,
          hours_worked: e.hours_worked,
          raw_tips: e.raw_tips,
          calculated_tips: e.calculated_tips,
        })),
        totalSales: parseFloat(totalSales) || 0,
        events: filteredEvents.map(({ ev, idx }) => ({
          id: ev.id,
          name: ev.name,
          total_gratuity: parseFloat(ev.total_gratuity) || 0,
          kitchen_tipout: kitchenTotalForEvent(idx),
        })),
        kitchenTipouts: filteredEvents.map(({ ev, idx }) => ({
          eventName: ev.name,
          entries: kitchenEmployees.map((ke) => ({
            employee_id: ke.id,
            employee_name: ke.name,
            amount: parseFloat(kitchenTips[idx]?.[ke.id] || "0") || 0,
          })),
        })),
        deletedEventIds,
      });
    } finally {
      setSaving(false);
    }
  }

  const hasData =
    dayEntries.some((e) => e.hours_worked > 0 || e.raw_tips > 0) ||
    parseFloat(totalSales) > 0;

  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-tribal-border px-4 py-2">
        <span className="text-sm font-semibold">{fmtDay(date)}</span>
        {locked && (
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300">
            Locked
          </span>
        )}
      </div>

      <div className="p-4">
        {/* Bartender rows */}
        <div className="mb-3 grid grid-cols-[1fr_5rem_6rem_5rem_6rem] gap-x-2 gap-y-1 text-xs">
          <span className="text-tribal-muted">Bartender</span>
          <span className="text-tribal-muted">Hours</span>
          <span className="text-tribal-muted">Raw Tips</span>
          <span className="text-tribal-muted">% Hrs</span>
          <span className="text-tribal-muted">Calc Tips</span>
          {employees.map((emp, i) => {
            const c = calc.entries[i];
            return (
              <div key={emp.id} className="contents">
                <span className="truncate py-1">{employeeDisplayName(emp)}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={entries[emp.id]?.hours ?? ""}
                  onChange={(e) => setField(emp.id, "hours", e.target.value)}
                  disabled={locked}
                  className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={entries[emp.id]?.tips ?? ""}
                  onChange={(e) => setField(emp.id, "tips", e.target.value)}
                  disabled={locked}
                  className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                />
                <span className="py-1 text-right tabular-nums text-tribal-muted">
                  {c ? pct(c.percent_of_hours) : "—"}
                </span>
                <span className="py-1 text-right tabular-nums font-medium">
                  {c ? money(c.calculated_tips) : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Sales */}
        <div className="mb-3 flex items-center gap-3">
          <label className="text-xs text-tribal-muted">Total Food/Bar Sales</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={totalSales}
            onChange={(e) => setTotalSales(e.target.value)}
            disabled={locked}
            className="w-32 rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right text-sm tabular-nums disabled:opacity-50"
          />
        </div>

        {/* Events */}
        {events.length > 0 && (
          <div className="mb-3 space-y-3">
            <span className="text-xs font-medium text-tribal-muted">Events / Gratuities</span>
            {events.map((ev, idx) => (
              <div key={idx} className="rounded-md border border-tribal-border/50 p-2 text-xs">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Event name"
                      value={ev.name}
                      onChange={(e) => setEventField(idx, "name", e.target.value)}
                      disabled={locked}
                      className="w-full rounded border border-tribal-border bg-tribal-bg px-2 py-1 disabled:opacity-50"
                    />
                  </div>
                  <div className="w-24">
                    <label className="text-[10px] text-tribal-muted">Gratuity</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ev.total_gratuity}
                      onChange={(e) => setEventField(idx, "total_gratuity", e.target.value)}
                      disabled={locked}
                      className="w-full rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                    />
                  </div>
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeEvent(idx)}
                      className="rounded p-1 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {kitchenEmployees.length > 0 && (
                  <div className="mt-2 border-t border-tribal-border/30 pt-2">
                    <span className="text-[10px] text-tribal-muted">Kitchen Tip-Outs</span>
                    <div className="mt-1 space-y-1">
                      {kitchenEmployees.map((ke) => (
                        <div key={ke.id} className="flex items-center gap-2">
                          <span className="w-24 truncate text-tribal-muted">{employeeDisplayName(ke)}</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={kitchenTips[idx]?.[ke.id] ?? ""}
                            onChange={(e) => setKitchenTip(idx, ke.id, e.target.value)}
                            disabled={locked}
                            placeholder="0"
                            className="w-20 rounded border border-tribal-border bg-tribal-bg px-2 py-0.5 text-right tabular-nums disabled:opacity-50"
                          />
                        </div>
                      ))}
                      {kitchenEmployees.length > 1 && (
                        <div className="flex items-center gap-2 text-[10px] text-tribal-muted">
                          <span className="w-24">Total</span>
                          <span className="w-20 text-right tabular-nums">{money(kitchenTotalForEvent(idx))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!locked && (
          <button
            type="button"
            onClick={addEvent}
            className="mb-3 flex items-center gap-1 text-xs text-tribal-muted hover:text-white"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Add Event
          </button>
        )}

        {/* Summary */}
        {hasData && (
          <div className="mt-2 rounded-md bg-white/[0.03] px-3 py-2 text-xs">
            <div className="flex justify-between">
              <span className="text-tribal-muted">Raw Tips Pool</span>
              <span className="tabular-nums">{money(calc.total_raw_tips)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-tribal-muted">Processing Fee ({processingFeePct}%)</span>
              <span className="tabular-nums text-red-400">−{money(calc.processing_fee)}</span>
            </div>
            {calc.event_net_to_pool > 0 && (
              <div className="flex justify-between">
                <span className="text-tribal-muted">Event Net to Pool</span>
                <span className="tabular-nums text-emerald-400">+{money(calc.event_net_to_pool)}</span>
              </div>
            )}
            <div className="mt-1 flex justify-between border-t border-tribal-border pt-1 font-medium">
              <span>Distributable Pool</span>
              <span className="tabular-nums">{money(calc.total_pool)}</span>
            </div>
            {parseFloat(totalSales) > 0 && (
              <div className="mt-1 flex justify-between text-tribal-muted">
                <span>Tip % of Sales</span>
                <span className="tabular-nums">
                  {((calc.total_raw_tips / parseFloat(totalSales)) * 100).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}

        {/* Save */}
        {!locked && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-tribal-red px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save Day"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
